<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { ref,computed,onMounted,onUnmounted } from 'vue'
import TutorialFrame from '@/components/Interface/Modal/Modals/Tutorial/TutorialFrame.vue'
import Alert from '@/components/Elements/Alerts/Alert.vue'
import { handleNext } from '../tutorialSteps'

const gameStore = useGameStore()
const playerFlagsStore = usePlayerFlagsStore()

const gameHasStarted = computed(() => gameStore.turn > 1)

const handleNextStep = async () => {
  await handleNext('tutorial_start')
}

// Reactive current time for countdown
const now = ref(new Date())
let interval: number | undefined

// Start interval on mount
onMounted(() => {
  interval = window.setInterval(() => {
    now.value = new Date()
  }, 1000)
})

// Cleanup on unmount
onUnmounted(() => {
  if (interval) {
    clearInterval(interval)
  }
})

// Calculate time left until arena starts
const timeLeft = computed(() => {
  const startTime = new Date(gameStore.startTime)
  const totalSeconds = Math.max(0, Math.floor((startTime.getTime() - now.value.getTime()) / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number) => n.toString().padStart(2, "0")

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
})

// Format local start time
const localStartTime = computed(() => {
  const startTime = new Date(gameStore.startTime)
  return startTime.toLocaleString(undefined, {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
})


</script>

<template>
  <TutorialFrame 
    v-if="!gameHasStarted"
    video-url="https://cdn.ferion.com/videos/advisors/temperate/exploration.webm"
  >
    <template #text>
      <p class="pb-2">
        Hello!
      </p>
      <p class="pb-2">
        It looks like the Ferion have blocked our ability to travel!
      </p>
      <p class="pb-2">
        But we are ready to receive your orders and will execute them as soon as the Ferion will allow us.
      </p>
    </template>

    <template #alerts>
      <Alert type="info">
        The arena will start <span class="text-green-500">{{ localStartTime }}</span>, which is in <span class="text-green-500">{{ timeLeft }}</span> hours.
      </Alert>
      <Alert type="warning" class="md:hidden">
        <p class="pb-2">It looks like you are on a mobile device.</p>
        While Ferion can be played on modern large screen mobile devices. Your experience will be better if you play the game on a laptop or tablet.
      </Alert>
    </template>

    <template #buttons>
      <ActionButton @click="handleNextStep">
        Understood!
      </ActionButton>
    </template>
  </TutorialFrame>

  <!-- In-game version (Day > 1) -->
  <TutorialFrame v-else
    video-url="https://cdn.ferion.com/videos/advisors/temperate/exploration.webm"
  >
    <template #text>
        <p class="pb-2">
          Hello!
        </p>
        <p class="pb-2">
          Something is not right! Our scientists have just discovered that the Ferion have transported us to this arena much later than other civilizations.
        </p>
        <p class="pb-2">
          We might be late to the party, but we will follow your orders to the end!
        </p>
    </template>

    <template #alerts>
      <Alert type="info">
        The game is already underway on Day {{ gameStore.turn }}. This puts you at a <span class="text-red-400">disadvantage</span>, we recommend you use this arena to learn the basics of the game and join the next arena from the start.
      </Alert>
      <Alert type="error" class="md:hidden">
        <p class="pb-2">It looks like you are on a mobile device.</p>
        While Ferion can be played on modern large screen mobile devices. Your experience will be better if you play the game on a laptop or tablet.
      </Alert>
    </template>

    <template #buttons>
      <ActionButton @click="handleNextStep">
        Understood!
      </ActionButton>
    </template>
  </TutorialFrame>
</template>

<style scoped lang="scss">

</style>